/**
 * AdminAiTopicPhotos — /admin/ai-topics/photos
 *
 * Admin queue for user-submitted photos on AI Topic pages. Each row
 * shows the photo, submitter, topic + a link to the live page. Two
 * actions: Approve (photo becomes public) or Hide (photo is kept in
 * the DB but never rendered).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, EyeOff, ExternalLink, Image as ImageIcon } from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminAiTopicPhotos() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoad] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = () => {
    setLoad(true);
    http.get("/admin/ai-topics/pending-photos?limit=200")
      .then((r) => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoad(false));
  };
  useEffect(() => { load(); }, []);

  const moderate = async (topicSlug, photoId, action) => {
    setBusyId(photoId);
    try {
      await http.post(`/admin/ai-topics/${topicSlug}/photos/${photoId}/moderate`, { action });
      toast.success(action === "approve" ? "Photo approved" : "Photo hidden");
      setItems((prev) => prev.filter((it) => it.photo?.id !== photoId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't apply moderation.");
    } finally { setBusyId(null); }
  };

  const bulkApprove = async (userId, userName) => {
    if (!window.confirm(`Approve every pending photo from ${userName}?`)) return;
    setBusyId(`bulk:${userId}`);
    try {
      const r = await http.post(`/admin/ai-topics/photos/bulk-moderate`,
                                 { user_id: userId, action: "approve" });
      toast.success(`Approved photos across ${r.data.topics_touched} topics for ${userName}`);
      setItems((prev) => prev.filter((it) => it.photo?.user_id !== userId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk approve failed.");
    } finally { setBusyId(null); }
  };

  if (!user || !(user.roles || []).includes("admin")) {
    return <div className="max-w-3xl mx-auto px-6 py-16 text-center text-sm opacity-70" data-testid="admin-photos-forbidden">Admin only.</div>;
  }

  return (
    <main className="max-w-5xl mx-auto px-5 lg:px-8 py-10" data-testid="admin-ai-topic-photos">
      <div className="flex items-center gap-2 mb-2">
        <ImageIcon className="w-5 h-5" />
        <h1 className="font-display text-3xl">AI Topic Photo Moderation</h1>
      </div>
      <p className="text-sm opacity-70 mb-6">
        Community-contributed photos awaiting review. Only approved photos render publicly.
      </p>

      {loading && <div className="text-sm opacity-70 py-8"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading queue…</div>}
      {!loading && items.length === 0 && (
        <div className="rounded-2xl border p-8 text-center text-sm opacity-70" style={{ borderColor: "var(--wofy-border)" }}>
          🎉 Queue clear — no pending photos right now.
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(({ topic_slug, topic_title, deep_link, photo }) => (
          <div
            key={photo.id}
            className="rounded-2xl border overflow-hidden"
            style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
            data-testid={`admin-photo-${photo.id}`}
          >
            <div className="aspect-video bg-black/5 overflow-hidden">
              <img src={photo.url} alt={photo.caption || ""} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="p-3 space-y-2">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">Topic</div>
                <Link to={deep_link || `/topic/${topic_slug}`} className="text-sm font-medium hover:underline inline-flex items-center gap-1">
                  {topic_title || topic_slug}
                  <ExternalLink className="w-3 h-3 opacity-60" />
                </Link>
              </div>
              <div className="text-xs opacity-70 flex items-center gap-2 flex-wrap">
                <span>By <span className="font-medium">{photo.user_name}</span> · {new Date(photo.created_at).toLocaleString()}</span>
                <button
                  type="button"
                  onClick={() => bulkApprove(photo.user_id, photo.user_name)}
                  className="text-[10px] underline opacity-70 hover:opacity-100"
                  data-testid={`admin-photo-trust-user-${photo.user_id}`}
                  title={`Approve every pending photo by ${photo.user_name}`}
                >
                  trust user
                </button>
              </div>
              {photo.caption && <div className="text-xs italic">"{photo.caption}"</div>}
              <div className="flex gap-2 pt-1">
                <button
                  onClick={() => moderate(topic_slug, photo.id, "approve")}
                  disabled={busyId === photo.id}
                  className="wofy-btn-primary text-xs flex-1 justify-center"
                  data-testid={`admin-photo-approve-${photo.id}`}
                >
                  {busyId === photo.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                  Approve
                </button>
                <button
                  onClick={() => moderate(topic_slug, photo.id, "hide")}
                  disabled={busyId === photo.id}
                  className="wofy-btn-ghost text-xs flex-1 justify-center"
                  data-testid={`admin-photo-hide-${photo.id}`}
                >
                  <EyeOff className="w-3 h-3" /> Hide
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}
